import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import Footer from "../footer/Footer";
import { fetchClientGdCategories, fetchClientGdTopics } from "../../services/clientGdService";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || "";
const DIFFICULTIES = ["BEGINNER", "INTERMEDIATE", "ADVANCED"];
const DIFF_COLORS = { BEGINNER: "#4caf50", INTERMEDIATE: "#ff9800", ADVANCED: "#e91e63" };
const DIFF_BG = { BEGINNER: "#f1faf1", INTERMEDIATE: "#fff8f0", ADVANCED: "#fff0f4" };

let debounceTimer;

const GdListPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchClientGdCategories().then((r) => {
      if (r.status === 200) setCategories(Array.isArray(r.data) ? r.data : []);
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, size: 10 };
    if (search) params.search = search;
    if (categoryId) params.categoryId = categoryId;
    if (difficulty) params.difficulty = difficulty;
    const res = await fetchClientGdTopics(params);
    if (res.status === 200 && res.data) {
      const d = res.data;
      setTopics(d.data || d.content || []);
      setTotalPages(d.totalPages || 0);
      setTotalItems(d.totalItems || d.totalElements || 0);
      setError("");
    } else {
      setTopics([]);
      setError(`Error ${res.status}: ${res.message || "Failed to load topics"}`);
    }
    setLoading(false);
  }, [page, search, categoryId, difficulty]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    clearTimeout(debounceTimer);
    const val = e.target.value;
    debounceTimer = setTimeout(() => { setPage(0); setSearch(val); }, 400);
  };

  return (
    <>
      <Navbar />

      {/* Page header */}
      <div style={{ background: "linear-gradient(135deg, #e8f5f5 0%, #f8fafc 100%)", borderBottom: "1px solid #eef0f3", padding: "40px 16px 32px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: "#1a1a2e", margin: "0 0 8px" }}>🗣️ Group Discussion</h1>
          <p style={{ fontSize: 16, color: "#555", margin: 0 }}>Explore topics, practice arguments, and sharpen your communication skills.</p>
        </div>
      </div>

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "28px 16px 48px" }}>

        {/* Category chips */}
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 10, marginBottom: 20 }}>
          <button
            onClick={() => { setPage(0); setCategoryId(""); }}
            style={{ flexShrink: 0, padding: "8px 20px", borderRadius: 999, border: `2px solid ${!categoryId ? "#019095" : "#ddd"}`, background: !categoryId ? "#019095" : "#fff", color: !categoryId ? "#fff" : "#555", cursor: "pointer", fontWeight: 700, fontSize: 14 }}>
            All
          </button>
          {categories.map((cat) => {
            const active = String(categoryId) === String(cat.id);
            return (
              <button key={cat.id}
                onClick={() => { setPage(0); setCategoryId(String(cat.id)); }}
                style={{ flexShrink: 0, padding: "8px 20px", borderRadius: 999, border: `2px solid ${active ? cat.color : "#ddd"}`, background: active ? cat.color : "#fff", color: active ? "#fff" : "#444", cursor: "pointer", fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s" }}>
                {cat.coverImageUrl && (
                  <img src={`${BASE_ROOT}${cat.coverImageUrl}`} alt={cat.name} style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover" }} />
                )}
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* Search + difficulty */}
        <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
          <input
            type="text" placeholder="Search topics..." onChange={handleSearch}
            style={{ flex: 1, minWidth: 220, padding: "11px 16px", borderRadius: 10, border: "1px solid #ddd", fontSize: 15, outline: "none" }}
          />
          <select value={difficulty} onChange={(e) => { setPage(0); setDifficulty(e.target.value); }}
            style={{ padding: "11px 16px", borderRadius: 10, border: "1px solid #ddd", fontSize: 15, background: "#fff", cursor: "pointer" }}>
            <option value="">All Levels</option>
            {DIFFICULTIES.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>

        {totalItems > 0 && (
          <p style={{ color: "#888", fontSize: 14, marginBottom: 20 }}>{totalItems} topic{totalItems !== 1 ? "s" : ""} found</p>
        )}

        {error ? (
          <div style={{ padding: "20px 24px", background: "#fff3f3", border: "1px solid #ffcdd2", borderRadius: 10, color: "#c62828", fontSize: 15 }}>
            <strong>Could not load topics.</strong> {error}
          </div>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#888", fontSize: 16 }}>Loading...</div>
        ) : topics.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#888", fontSize: 16 }}>No topics found.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {topics.map((t) => {
              const isComingSoon = t.status === "COMING_SOON";
              // thumbnailUrl first, fallback to category cover
              const imgUrl = t.thumbnailUrl || t.category?.coverImageUrl;
              return (
                <div
                  key={t.id}
                  onClick={() => navigate(`/gd/${t.id}`)}
                  style={{
                    borderRadius: 14, overflow: "hidden",
                    border: "1px solid #eef0f3",
                    cursor: "pointer",
                    background: "#fff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                    transition: "box-shadow 0.2s, transform 0.2s",
                    opacity: isComingSoon ? 0.82 : 1,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)"; e.currentTarget.style.transform = "none"; }}
                >
                  {/* Thumbnail */}
                  <div style={{ height: 160, overflow: "hidden", position: "relative", background: t.category?.color ? t.category.color + "22" : "#f0f4f8" }}>
                    {imgUrl ? (
                      <img
                        src={`${BASE_ROOT}${imgUrl}`}
                        alt={t.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40 }}>🗣️</div>
                    )}
                    {/* Difficulty badge overlay */}
                    <span style={{
                      position: "absolute", top: 10, right: 10,
                      fontSize: 11, fontWeight: 700,
                      color: DIFF_COLORS[t.difficulty],
                      background: DIFF_BG[t.difficulty],
                      border: `1px solid ${DIFF_COLORS[t.difficulty]}55`,
                      borderRadius: 999, padding: "3px 10px",
                    }}>{t.difficulty}</span>
                    {/* Coming soon overlay */}
                    {isComingSoon && (
                      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
                        <span style={{ fontSize: 28 }}>🔒</span>
                        <span style={{ color: "#fff", fontWeight: 700, fontSize: 14, letterSpacing: 0.5 }}>Coming Soon</span>
                      </div>
                    )}
                    {/* Category color bar */}
                    {t.category?.color && (
                      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: t.category.color }} />
                    )}
                  </div>

                  {/* Card body */}
                  <div style={{ padding: "14px 16px 16px" }}>
                    {t.category && (
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: t.category.color,
                        background: t.category.color + "18",
                        borderRadius: 999, padding: "2px 10px",
                        display: "inline-block", marginBottom: 8,
                      }}>
                        {t.category.name}
                      </span>
                    )}
                    <h3 style={{
                      fontSize: 16, fontWeight: 700,
                      margin: "0 0 10px",
                      color: isComingSoon ? "#999" : "#1a1a2e",
                      lineHeight: 1.4,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}>{t.title}</h3>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 12, color: "#aaa" }}>
                        {t.createdAt ? new Date(t.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                      </span>
                      {!isComingSoon && (
                        <span style={{ fontSize: 12, color: "#019095", fontWeight: 700 }}>Read more →</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 14, marginTop: 36 }}>
            <button disabled={page === 0} onClick={() => setPage(page - 1)}
              style={{ padding: "10px 24px", borderRadius: 8, border: "1px solid #019095", color: page === 0 ? "#bbb" : "#019095", borderColor: page === 0 ? "#ddd" : "#019095", background: "#fff", cursor: page === 0 ? "default" : "pointer", fontWeight: 600, fontSize: 14 }}>
              ← Prev
            </button>
            <span style={{ fontSize: 14, color: "#555" }}>Page {page + 1} / {totalPages}</span>
            <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}
              style={{ padding: "10px 24px", borderRadius: 8, border: "1px solid #019095", color: page + 1 >= totalPages ? "#bbb" : "#019095", borderColor: page + 1 >= totalPages ? "#ddd" : "#019095", background: "#fff", cursor: page + 1 >= totalPages ? "default" : "pointer", fontWeight: 600, fontSize: 14 }}>
              Next →
            </button>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default GdListPage;
